import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Container from '@components/Container';
import Button from '@components/Button';
import Modal from '@components/Modal';
import {
  deleteVisitEvent,
  deleteVisitEvents,
  fetchVisitAnalytics,
  type DailyVisitCount,
  type VisitAnalytics,
} from '@lib/visits';
import { getUserSessions, type BreathingSession } from '@lib/sessions';
import {
  fetchDashboardEvents,
  type DashboardEventRow,
} from '@lib/dashboardEvents';
import {
  fetchBreathingEvents,
  type BreathingEventRow,
} from '@lib/breathingEvents';
import {
  fetchVisionBoardEvents,
  type VisionBoardEventRow,
} from '@lib/visionBoardEvents';
import {
  fetchDashboardPageTimeEntries,
  type DashboardPageTimeEntry,
} from '@lib/dashboardPageTime';
import {
  fetchBreathingPageTimeEntries,
  type BreathingPageTimeEntry,
} from '@lib/breathingPageTime';
import {
  fetchVisionBoardPageTimeEntries,
  type VisionBoardPageTimeEntry,
} from '@lib/visionBoardPageTime';

interface ChartPoint {
  key: string;
  label: string;
  fullLabel: string;
  value: number;
  axisDate: string;
}

const RECENT_VISITS_PAGE_SIZE = 10;
const DASHBOARD_LIST_PAGE_SIZE = 10;

function AdminVisits() {
  const [analytics, setAnalytics] = useState<VisitAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);
  const [confirmingVisitId, setConfirmingVisitId] = useState<string | null>(
    null
  );
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [period, setPeriod] = useState<'day' | 'month' | 'year'>('day');
  const [showUsersOnly, setShowUsersOnly] = useState(false);
  const [referrerQuery, setReferrerQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(RECENT_VISITS_PAGE_SIZE);
  const [sessionsCache, setSessionsCache] = useState<
    Record<string, BreathingSession[] | null | undefined>
  >({});
  const [isMobile, setIsMobile] = useState(false);
  const [hideAdminVisits, setHideAdminVisits] = useState(true);
  const [hideAdminDashboardEvents, setHideAdminDashboardEvents] =
    useState(true);
  const [showStartClickOnly, setShowStartClickOnly] = useState(false);
  const [hideAdminPageTime, setHideAdminPageTime] = useState(true);
  const [hideInProgressPageTime, setHideInProgressPageTime] = useState(true);
  const [visitorIdFilter, setVisitorIdFilter] = useState('');
  const [dashboardEvents, setDashboardEvents] = useState<DashboardEventRow[]>(
    []
  );
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [dashboardEventsVisibleCount, setDashboardEventsVisibleCount] =
    useState(DASHBOARD_LIST_PAGE_SIZE);
  const [breathingEvents, setBreathingEvents] = useState<BreathingEventRow[]>(
    []
  );
  const [breathingEventsLoading, setBreathingEventsLoading] = useState(true);
  const [breathingEventsError, setBreathingEventsError] = useState<
    string | null
  >(null);
  const [breathingEventsVisibleCount, setBreathingEventsVisibleCount] =
    useState(DASHBOARD_LIST_PAGE_SIZE);
  const [hideAdminBreathingEvents, setHideAdminBreathingEvents] =
    useState(true);
  const [visionBoardEvents, setVisionBoardEvents] = useState<
    VisionBoardEventRow[]
  >([]);
  const [visionBoardEventsLoading, setVisionBoardEventsLoading] =
    useState(true);
  const [visionBoardEventsError, setVisionBoardEventsError] = useState<
    string | null
  >(null);
  const [visionBoardEventsVisibleCount, setVisionBoardEventsVisibleCount] =
    useState(DASHBOARD_LIST_PAGE_SIZE);
  const [hideAdminVisionBoardEvents, setHideAdminVisionBoardEvents] =
    useState(true);
  const [pageTimeEntries, setPageTimeEntries] = useState<
    DashboardPageTimeEntry[]
  >([]);
  const [pageTimeLoading, setPageTimeLoading] = useState(true);
  const [pageTimeError, setPageTimeError] = useState<string | null>(null);
  const [pageTimeVisibleCount, setPageTimeVisibleCount] = useState(
    DASHBOARD_LIST_PAGE_SIZE
  );
  const [breathingPageTimeEntries, setBreathingPageTimeEntries] = useState<
    BreathingPageTimeEntry[]
  >([]);
  const [breathingPageTimeLoading, setBreathingPageTimeLoading] =
    useState(true);
  const [breathingPageTimeError, setBreathingPageTimeError] = useState<
    string | null
  >(null);
  const [breathingPageTimeVisibleCount, setBreathingPageTimeVisibleCount] =
    useState(DASHBOARD_LIST_PAGE_SIZE);
  const [hideAdminBreathingPageTime, setHideAdminBreathingPageTime] =
    useState(true);
  const [hideInProgressBreathingPageTime, setHideInProgressBreathingPageTime] =
    useState(true);
  const [visionBoardPageTimeEntries, setVisionBoardPageTimeEntries] = useState<
    VisionBoardPageTimeEntry[]
  >([]);
  const [visionBoardPageTimeLoading, setVisionBoardPageTimeLoading] =
    useState(true);
  const [visionBoardPageTimeError, setVisionBoardPageTimeError] = useState<
    string | null
  >(null);
  const [visionBoardPageTimeVisibleCount, setVisionBoardPageTimeVisibleCount] =
    useState(DASHBOARD_LIST_PAGE_SIZE);
  const [hideAdminVisionBoardPageTime, setHideAdminVisionBoardPageTime] =
    useState(true);
  const [
    hideInProgressVisionBoardPageTime,
    setHideInProgressVisionBoardPageTime,
  ] = useState(true);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const count = analytics?.dailyCounts?.length ?? 0;
    if (!loading && count > 0 && chartContainerRef.current) {
      // Scroll to the far right so latest days are visible
      chartContainerRef.current.scrollLeft =
        chartContainerRef.current.scrollWidth;
    }
  }, [loading, analytics?.dailyCounts?.length, period]);

  useEffect(() => {
    loadAnalytics();
    loadDashboardEvents();
    loadBreathingEvents();
    loadVisionBoardEvents();
    loadDashboardPageTime();
    loadBreathingPageTime();
    loadVisionBoardPageTime();
    // detect small screens by window width (keep in sync with Tailwind 'sm')
    const updateIsMobile = () => setIsMobile(window.innerWidth < 640);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchVisitAnalytics();
      setAnalytics(data);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load visit analytics at the moment.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardEvents = async () => {
    setEventsLoading(true);
    setEventsError(null);
    try {
      const events = await fetchDashboardEvents(200);
      setDashboardEvents(events);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load dashboard events right now.';
      setEventsError(message);
    } finally {
      setEventsLoading(false);
    }
  };

  const loadBreathingEvents = async () => {
    setBreathingEventsLoading(true);
    setBreathingEventsError(null);
    try {
      const events = await fetchBreathingEvents(200);
      setBreathingEvents(events);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load breathing events right now.';
      setBreathingEventsError(message);
    } finally {
      setBreathingEventsLoading(false);
    }
  };

  const loadVisionBoardEvents = async () => {
    setVisionBoardEventsLoading(true);
    setVisionBoardEventsError(null);
    try {
      const events = await fetchVisionBoardEvents(200);
      setVisionBoardEvents(events);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load vision board events right now.';
      setVisionBoardEventsError(message);
    } finally {
      setVisionBoardEventsLoading(false);
    }
  };

  const loadDashboardPageTime = async () => {
    setPageTimeLoading(true);
    setPageTimeError(null);
    try {
      const entries = await fetchDashboardPageTimeEntries(200);
      setPageTimeEntries(entries);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load dashboard page time right now.';
      setPageTimeError(message);
    } finally {
      setPageTimeLoading(false);
    }
  };

  const loadBreathingPageTime = async () => {
    setBreathingPageTimeLoading(true);
    setBreathingPageTimeError(null);
    try {
      const entries = await fetchBreathingPageTimeEntries(200);
      setBreathingPageTimeEntries(entries);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load breathing page time right now.';
      setBreathingPageTimeError(message);
    } finally {
      setBreathingPageTimeLoading(false);
    }
  };

  const loadVisionBoardPageTime = async () => {
    setVisionBoardPageTimeLoading(true);
    setVisionBoardPageTimeError(null);
    try {
      const entries = await fetchVisionBoardPageTimeEntries(200);
      setVisionBoardPageTimeEntries(entries);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load vision board page time right now.';
      setVisionBoardPageTimeError(message);
    } finally {
      setVisionBoardPageTimeLoading(false);
    }
  };

  const chartPoints = useMemo<ChartPoint[]>(() => {
    if (!analytics?.dailyCounts) return [];

    // Aggregate daily counts into day / month / year buckets based on `period`.
    const map = new Map<string, { date: Date; value: number }>();

    analytics.dailyCounts.forEach((point: DailyVisitCount) => {
      const d = new Date(point.visit_date);
      if (Number.isNaN(d.getTime())) return;

      let key: string;
      if (period === 'day') {
        key = d.toISOString().slice(0, 10); // YYYY-MM-DD
      } else if (period === 'month') {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM
      } else {
        key = String(d.getFullYear()); // YYYY
      }

      const existing = map.get(key);
      const value = Number(point.total_visits) || 0;
      if (existing) {
        existing.value += value;
      } else {
        // store a representative date for formatting later (use first encountered)
        map.set(key, { date: d, value });
      }
    });

    // Convert map to sorted array by date
    const arr = Array.from(map.entries())
      .map(([k, v]) => ({ key: k, date: v.date, value: v.value }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return arr.map((item) => {
      let label = '';
      let fullLabel = '';
      let axisDate = '';
      const d = item.date;

      if (period === 'day') {
        axisDate = d.toISOString().slice(0, 10);
        label = new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric',
        }).format(d);
        fullLabel = new Intl.DateTimeFormat(undefined, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        }).format(d);
      } else if (period === 'month') {
        axisDate = new Date(d.getFullYear(), d.getMonth(), 1)
          .toISOString()
          .slice(0, 10);
        label = new Intl.DateTimeFormat(undefined, {
          month: 'short',
          year: 'numeric',
        }).format(d);
        fullLabel = new Intl.DateTimeFormat(undefined, {
          month: 'long',
          year: 'numeric',
        }).format(d);
      } else {
        axisDate = new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
        label = new Intl.DateTimeFormat(undefined, {
          year: 'numeric',
        }).format(d);
        fullLabel = label;
      }

      return {
        key: item.key,
        label,
        fullLabel,
        value: item.value,
        axisDate,
      } as ChartPoint;
    });
  }, [analytics?.dailyCounts, period]);

  const countryCounts = useMemo(() => {
    if (!analytics?.recentVisits)
      return [] as {
        key: string;
        iso: string | null;
        name: string;
        count: number;
      }[];
    const map = new Map<
      string,
      { iso: string | null; name: string; count: number }
    >();
    analytics.recentVisits.forEach((v) => {
      const raw = (v.country || 'Unknown').trim() || 'Unknown';
      const iso = extractIsoCode(raw);
      const key = iso ?? raw;
      const name = iso ? getRegionName(iso) : raw;
      const entry = map.get(key);
      if (entry) {
        entry.count += 1;
      } else {
        map.set(key, { iso, name, count: 1 });
      }
    });
    return Array.from(map.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => b.count - a.count);
  }, [analytics?.recentVisits]);

  const deviceCounts = useMemo(() => {
    if (!analytics?.recentVisits)
      return [] as { device: string; count: number }[];
    const map = new Map<string, number>();
    analytics.recentVisits.forEach((v) => {
      const deviceRaw = (v.device || '').trim();
      const isMobile = /android|iphone|ipad|ipod|mobile|mobi/i.test(deviceRaw);
      const key = isMobile ? 'Mobile' : 'Desktop';
      map.set(key, (map.get(key) ?? 0) + 1);
    });

    return Array.from(map.entries())
      .map(([device, count]) => ({ device, count }))
      .sort((a, b) => b.count - a.count);
  }, [analytics?.recentVisits]);

  const userCounts = useMemo(() => {
    if (!analytics?.recentVisits)
      return [] as { key: string; name: string; count: number }[];
    const map = new Map<string, { name: string; count: number }>();
    analytics.recentVisits.forEach((v) => {
      const rawName = (v.display_name ?? '').trim();
      // skip empty / N/A display names
      if (!rawName) return;
      const key = rawName;
      const entry = map.get(key);
      if (entry) {
        entry.count += 1;
      } else {
        map.set(key, { name: rawName, count: 1 });
      }
    });
    return Array.from(map.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => b.count - a.count);
  }, [analytics?.recentVisits]);

  const utmCounts = useMemo(() => {
    if (!analytics?.recentVisits) return [] as { utm: string; count: number }[];
    const map = new Map<string, number>();
    analytics.recentVisits.forEach((v) => {
      const utm = (v.utm_source || '').trim();
      if (!utm) return; // skip null/empty (do not count N/A)
      map.set(utm, (map.get(utm) ?? 0) + 1);
    });
    return Array.from(map.entries())
      .map(([utm, count]) => ({ utm, count }))
      .sort((a, b) => b.count - a.count);
  }, [analytics?.recentVisits]);

  const pageCounts = useMemo(() => {
    if (!analytics?.recentVisits)
      return [] as { page: string; count: number }[];
    const map = new Map<string, number>();

    analytics.recentVisits.forEach((v) => {
      const raw = (v.referrer ?? '').trim();
      if (!raw) return; // skip null/empty

      try {
        const u = new URL(raw);
        const head = u.pathname.split('/').filter(Boolean)[0];
        const key = head ? `/${head}` : '/';
        map.set(key, (map.get(key) ?? 0) + 1);
      } catch {
        // fallback to a trimmed snippet of the referrer string
        const snippet = raw.split('/').slice(0, 3).join('/');
        const key = snippet || raw;
        map.set(key, (map.get(key) ?? 0) + 1);
      }
    });

    return Array.from(map.entries())
      .map(([page, count]) => ({ page, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [analytics?.recentVisits]);

  const filteredRecentVisits = useMemo(() => {
    const recent = analytics?.recentVisits ?? [];

    // Apply authenticated user filter first
    let filtered = showUsersOnly
      ? recent.filter((visit) => Boolean(visit.user_id))
      : recent.slice();

    if (hideAdminVisits) {
      filtered = filtered.filter((visit) => !visit.is_admin);
    }

    // Apply referrer search if present (case-insensitive substring match)
    const q = (referrerQuery || '').trim().toLowerCase();
    if (q.length > 0) {
      filtered = filtered.filter((visit) => {
        const ref = (visit.referrer || '').toLowerCase();
        return ref.includes(q);
      });
    }

    return filtered;
  }, [analytics?.recentVisits, showUsersOnly, referrerQuery, hideAdminVisits]);

  const visibleRecentVisits = useMemo(() => {
    return filteredRecentVisits.slice(0, visibleCount);
  }, [filteredRecentVisits, visibleCount]);

  const hasMoreVisits =
    visibleRecentVisits.length < filteredRecentVisits.length;

  useEffect(() => {
    setVisibleCount(RECENT_VISITS_PAGE_SIZE);
  }, [showUsersOnly, hideAdminVisits, filteredRecentVisits.length]);

  // Fetch recent sessions for visible signed-in users so we can attempt to
  // derive session durations based on nearby session rows.
  useEffect(() => {
    const userIds = Array.from(
      new Set(
        visibleRecentVisits.map((v) => v.user_id).filter(Boolean) as string[]
      )
    );
    const toFetch = userIds.filter((id) => !(id in sessionsCache));
    if (toFetch.length === 0) return;

    (async () => {
      // Mark as loading (null) so we don't re-fetch repeatedly
      setSessionsCache((prev) => {
        const next = { ...prev };
        toFetch.forEach((id) => (next[id] = null));
        return next;
      });

      await Promise.all(
        toFetch.map(async (id) => {
          try {
            const rows = await getUserSessions(id, 10);
            setSessionsCache((prev) => ({ ...prev, [id]: rows || [] }));
          } catch (err) {
            setSessionsCache((prev) => ({ ...prev, [id]: [] }));
          }
        })
      );
    })();
  }, [visibleRecentVisits, sessionsCache]);

  const handleDelete = async (visitId: string) => {
    if (!analytics) return;
    const visit = analytics.recentVisits.find((item) => item.id === visitId);

    setDeletingId(visitId);
    setError(null);
    try {
      await deleteVisitEvent(visitId);

      setAnalytics((prev) => {
        if (!prev) return prev;

        const dateKey = visit ? normalizeDateKey(visit.occurred_at) : null;
        const updatedDaily =
          dateKey && prev.dailyCounts
            ? prev.dailyCounts
                .map((day) =>
                  day.visit_date === dateKey
                    ? {
                        ...day,
                        total_visits: Math.max(0, day.total_visits - 1),
                      }
                    : day
                )
                .filter((day) => day.total_visits > 0)
            : prev.dailyCounts;

        return {
          totalVisits: Math.max(0, prev.totalVisits - 1),
          dailyCounts: updatedDaily,
          recentVisits: prev.recentVisits.filter((item) => item.id !== visitId),
        };
      });
    } catch (_err) {
      setError('Failed to delete visit. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleBulkDelete = async (ids: string[]) => {
    if (!analytics || ids.length === 0) return;
    setError(null);
    setIsDeletingMultiple(true);
    try {
      await deleteVisitEvents(ids);

      setAnalytics((prev) => {
        if (!prev) return prev;

        // Build a map of dateKey -> removedCount for adjusting dailyCounts
        const removedByDate = new Map<string, number>();
        prev.recentVisits.forEach((rv) => {
          if (ids.includes(rv.id)) {
            const key = normalizeDateKey(rv.occurred_at);
            if (!key) return;
            removedByDate.set(key, (removedByDate.get(key) ?? 0) + 1);
          }
        });

        const updatedDaily = prev.dailyCounts
          ? prev.dailyCounts
              .map((day) => {
                const rem = removedByDate.get(day.visit_date) ?? 0;
                return {
                  ...day,
                  total_visits: Math.max(0, day.total_visits - rem),
                };
              })
              .filter((day) => day.total_visits > 0)
          : prev.dailyCounts;

        return {
          totalVisits: Math.max(0, prev.totalVisits - ids.length),
          dailyCounts: updatedDaily,
          recentVisits: prev.recentVisits.filter(
            (item) => !ids.includes(item.id)
          ),
        };
      });

      // Clear selection
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
      setConfirmingBulk(false);
    } catch (_err) {
      setError('Failed to delete visits. Please try again.');
    } finally {
      setIsDeletingMultiple(false);
    }
  };

  const openConfirm = (visitId: string) => setConfirmingVisitId(visitId);
  const closeConfirm = () => setConfirmingVisitId(null);

  const totalVisitsDisplay = analytics
    ? analytics.totalVisits.toLocaleString()
    : '--';

  const title =
    period === 'day'
      ? 'Daily Breakdown'
      : period === 'month'
        ? 'Monthly Breakdown'
        : 'Yearly Breakdown';
  const subtitle = `All-time visits per ${period} with a clean, smooth line.`;

  const normalizedVisitorFilter = useMemo(
    () => visitorIdFilter.trim().toLowerCase(),
    [visitorIdFilter]
  );

  const matchesVisitorFilter = useCallback(
    (value?: string | null): boolean => {
      if (!normalizedVisitorFilter) return true;
      return (value ?? '').toLowerCase().includes(normalizedVisitorFilter);
    },
    [normalizedVisitorFilter]
  );

  const filteredDashboardEvents = useMemo<DashboardEventRow[]>(() => {
    let filtered = hideAdminDashboardEvents
      ? dashboardEvents.filter((e) => !e.is_admin)
      : dashboardEvents;
    if (showStartClickOnly) {
      filtered = filtered.filter((e) => e.event_type === 'start_click');
    }
    filtered = filtered.filter((event) =>
      matchesVisitorFilter(event.visitor_id)
    );
    return filtered;
  }, [
    dashboardEvents,
    hideAdminDashboardEvents,
    showStartClickOnly,
    matchesVisitorFilter,
  ]);

  const visibleDashboardEvents = useMemo(() => {
    return filteredDashboardEvents.slice(0, dashboardEventsVisibleCount);
  }, [filteredDashboardEvents, dashboardEventsVisibleCount]);

  const hasMoreDashboardEvents =
    visibleDashboardEvents.length < filteredDashboardEvents.length;

  const filteredBreathingEvents = useMemo<BreathingEventRow[]>(() => {
    let filtered = hideAdminBreathingEvents
      ? breathingEvents.filter((e) => !e.is_admin)
      : breathingEvents;
    filtered = filtered.filter((event) =>
      matchesVisitorFilter(event.visitor_id)
    );
    return filtered;
  }, [breathingEvents, hideAdminBreathingEvents, matchesVisitorFilter]);

  const visibleBreathingEvents = useMemo(() => {
    return filteredBreathingEvents.slice(0, breathingEventsVisibleCount);
  }, [filteredBreathingEvents, breathingEventsVisibleCount]);

  const hasMoreBreathingEvents =
    visibleBreathingEvents.length < filteredBreathingEvents.length;

  const filteredVisionBoardEvents = useMemo<VisionBoardEventRow[]>(() => {
    let filtered = hideAdminVisionBoardEvents
      ? visionBoardEvents.filter((e) => !e.is_admin)
      : visionBoardEvents;
    filtered = filtered.filter((event) =>
      matchesVisitorFilter(event.visitor_id)
    );
    return filtered;
  }, [visionBoardEvents, hideAdminVisionBoardEvents, matchesVisitorFilter]);

  const visibleVisionBoardEvents = useMemo(() => {
    return filteredVisionBoardEvents.slice(0, visionBoardEventsVisibleCount);
  }, [filteredVisionBoardEvents, visionBoardEventsVisibleCount]);

  const hasMoreVisionBoardEvents =
    visibleVisionBoardEvents.length < filteredVisionBoardEvents.length;

  const filteredDashboardPageTimes = useMemo<DashboardPageTimeEntry[]>(() => {
    let filtered = hideAdminPageTime
      ? pageTimeEntries.filter((entry) => !entry.is_admin)
      : pageTimeEntries;
    if (hideInProgressPageTime) {
      filtered = filtered.filter((entry) => Boolean(entry.exited_at));
    }
    filtered = filtered.filter((entry) =>
      matchesVisitorFilter(entry.visitor_id)
    );
    return filtered;
  }, [
    pageTimeEntries,
    hideAdminPageTime,
    hideInProgressPageTime,
    matchesVisitorFilter,
  ]);

  const visibleDashboardPageTimes = useMemo(() => {
    return filteredDashboardPageTimes.slice(0, pageTimeVisibleCount);
  }, [filteredDashboardPageTimes, pageTimeVisibleCount]);

  const hasMoreDashboardPageTimes =
    visibleDashboardPageTimes.length < filteredDashboardPageTimes.length;

  const filteredBreathingPageTimes = useMemo<BreathingPageTimeEntry[]>(() => {
    let filtered = hideAdminBreathingPageTime
      ? breathingPageTimeEntries.filter((entry) => !entry.is_admin)
      : breathingPageTimeEntries;
    if (hideInProgressBreathingPageTime) {
      filtered = filtered.filter((entry) => Boolean(entry.exited_at));
    }
    filtered = filtered.filter((entry) =>
      matchesVisitorFilter(entry.visitor_id)
    );
    return filtered;
  }, [
    breathingPageTimeEntries,
    hideAdminBreathingPageTime,
    hideInProgressBreathingPageTime,
    matchesVisitorFilter,
  ]);

  const visibleBreathingPageTimes = useMemo(() => {
    return filteredBreathingPageTimes.slice(0, breathingPageTimeVisibleCount);
  }, [filteredBreathingPageTimes, breathingPageTimeVisibleCount]);

  const hasMoreBreathingPageTimes =
    visibleBreathingPageTimes.length < filteredBreathingPageTimes.length;

  const filteredVisionBoardPageTimes = useMemo<
    VisionBoardPageTimeEntry[]
  >(() => {
    let filtered = hideAdminVisionBoardPageTime
      ? visionBoardPageTimeEntries.filter((entry) => !entry.is_admin)
      : visionBoardPageTimeEntries;
    if (hideInProgressVisionBoardPageTime) {
      filtered = filtered.filter((entry) => Boolean(entry.exited_at));
    }
    filtered = filtered.filter((entry) =>
      matchesVisitorFilter(entry.visitor_id)
    );
    return filtered;
  }, [
    visionBoardPageTimeEntries,
    hideAdminVisionBoardPageTime,
    hideInProgressVisionBoardPageTime,
    matchesVisitorFilter,
  ]);

  const visibleVisionBoardPageTimes = useMemo(() => {
    return filteredVisionBoardPageTimes.slice(
      0,
      visionBoardPageTimeVisibleCount
    );
  }, [filteredVisionBoardPageTimes, visionBoardPageTimeVisibleCount]);

  const hasMoreVisionBoardPageTimes =
    visibleVisionBoardPageTimes.length < filteredVisionBoardPageTimes.length;

  const hiddenAdminPageTimeCount = useMemo(() => {
    return hideAdminPageTime
      ? pageTimeEntries.filter((entry) => entry.is_admin).length
      : 0;
  }, [pageTimeEntries, hideAdminPageTime]);

  const hiddenInProgressPageTimeCount = useMemo(() => {
    if (!hideInProgressPageTime) return 0;
    const base = hideAdminPageTime
      ? pageTimeEntries.filter((entry) => !entry.is_admin)
      : pageTimeEntries;
    return base.filter((entry) => !entry.exited_at).length;
  }, [pageTimeEntries, hideAdminPageTime, hideInProgressPageTime]);

  const hiddenAdminBreathingPageTimeCount = useMemo(() => {
    return hideAdminBreathingPageTime
      ? breathingPageTimeEntries.filter((entry) => entry.is_admin).length
      : 0;
  }, [breathingPageTimeEntries, hideAdminBreathingPageTime]);

  const hiddenInProgressBreathingPageTimeCount = useMemo(() => {
    if (!hideInProgressBreathingPageTime) return 0;
    const base = hideAdminBreathingPageTime
      ? breathingPageTimeEntries.filter((entry) => !entry.is_admin)
      : breathingPageTimeEntries;
    return base.filter((entry) => !entry.exited_at).length;
  }, [
    breathingPageTimeEntries,
    hideAdminBreathingPageTime,
    hideInProgressBreathingPageTime,
  ]);

  const hiddenAdminVisionBoardPageTimeCount = useMemo(() => {
    return hideAdminVisionBoardPageTime
      ? visionBoardPageTimeEntries.filter((entry) => entry.is_admin).length
      : 0;
  }, [visionBoardPageTimeEntries, hideAdminVisionBoardPageTime]);

  const hiddenInProgressVisionBoardPageTimeCount = useMemo(() => {
    if (!hideInProgressVisionBoardPageTime) return 0;
    const base = hideAdminVisionBoardPageTime
      ? visionBoardPageTimeEntries.filter((entry) => !entry.is_admin)
      : visionBoardPageTimeEntries;
    return base.filter((entry) => !entry.exited_at).length;
  }, [
    visionBoardPageTimeEntries,
    hideAdminVisionBoardPageTime,
    hideInProgressVisionBoardPageTime,
  ]);

  useEffect(() => {
    setDashboardEventsVisibleCount(DASHBOARD_LIST_PAGE_SIZE);
  }, [
    filteredDashboardEvents.length,
    hideAdminDashboardEvents,
    showStartClickOnly,
    visitorIdFilter,
  ]);

  useEffect(() => {
    setBreathingEventsVisibleCount(DASHBOARD_LIST_PAGE_SIZE);
  }, [
    filteredBreathingEvents.length,
    hideAdminBreathingEvents,
    visitorIdFilter,
  ]);

  useEffect(() => {
    setVisionBoardEventsVisibleCount(DASHBOARD_LIST_PAGE_SIZE);
  }, [
    filteredVisionBoardEvents.length,
    hideAdminVisionBoardEvents,
    visitorIdFilter,
  ]);

  useEffect(() => {
    setBreathingPageTimeVisibleCount(DASHBOARD_LIST_PAGE_SIZE);
  }, [
    filteredBreathingPageTimes.length,
    hideAdminBreathingPageTime,
    hideInProgressBreathingPageTime,
    visitorIdFilter,
  ]);

  useEffect(() => {
    setVisionBoardPageTimeVisibleCount(DASHBOARD_LIST_PAGE_SIZE);
  }, [
    filteredVisionBoardPageTimes.length,
    hideAdminVisionBoardPageTime,
    hideInProgressVisionBoardPageTime,
    visitorIdFilter,
  ]);

  useEffect(() => {
    setPageTimeVisibleCount(DASHBOARD_LIST_PAGE_SIZE);
  }, [
    filteredDashboardPageTimes.length,
    hideAdminPageTime,
    hideInProgressPageTime,
    visitorIdFilter,
  ]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      {/* Animated background gradient overlay */}
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
      />
      <Container className="relative z-10 py-10">
        <div className="mx-auto max-w-5xl pt-4 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Visit Analytics{' '}
          </h1>
          <p className="mt-2 pb-10 text-base text-gray-600">
            All-time visits across{' '}
            <span className="logo-font font-bold">Xhalin</span>. No filters,
            just the raw story.
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="grid gap-6">
          <section className="overflow-visible rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
            <div className="flex flex-col items-center justify-between gap-6 sm:flex-row sm:items-start">
              <div className="flex-1">
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Total Visits
                </p>
                <p className="mt-3 text-4xl font-bold text-indigo-600 sm:text-6xl">
                  {loading ? '...' : totalVisitsDisplay}
                </p>
              </div>
              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-5">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    Visits by User
                  </p>
                  <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    {loading ? (
                      <div className="text-sm text-gray-600">Loading...</div>
                    ) : userCounts.length === 0 ? (
                      <div className="text-sm text-gray-600">No user data</div>
                    ) : (
                      <ul className="text-sm text-gray-800">
                        {userCounts.slice(0, 6).map((u) => (
                          <li
                            key={u.key}
                            className="flex items-center justify-between py-1"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {u.name}
                            </span>
                            <span className="ml-4 font-semibold text-indigo-600">
                              {u.count.toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    Visits by Country
                  </p>
                  <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    {loading ? (
                      <div className="text-sm text-gray-600">Loading...</div>
                    ) : countryCounts.length === 0 ? (
                      <div className="text-sm text-gray-600">
                        No country data
                      </div>
                    ) : (
                      <ul className="text-sm text-gray-800">
                        {countryCounts.slice(0, 6).map((c) => {
                          const iso = c.iso;
                          const countryLabel = c.name || 'Unknown';
                          const flagSrc = iso
                            ? `https://flagcdn.com/24x18/${iso.toLowerCase()}.png`
                            : `https://countryflagsapi.com/png/${encodeURIComponent(
                                countryLabel
                              )}`;
                          const flagEmoji = flagEmojiFromCode(iso ?? '');

                          return (
                            <li
                              key={c.key}
                              className="flex items-center justify-between py-1"
                            >
                              <span className="flex min-w-0 items-center gap-2 text-sm text-gray-800">
                                <span className="flex items-center gap-2">
                                  <img
                                    src={flagSrc}
                                    alt={countryLabel}
                                    title={countryLabel}
                                    className="h-4 w-6 flex-shrink-0 rounded-sm object-cover"
                                    onError={(e) => {
                                      (
                                        e.currentTarget as HTMLImageElement
                                      ).style.display = 'none';
                                    }}
                                  />
                                  {flagEmoji ? (
                                    <span className="text-sm">{flagEmoji}</span>
                                  ) : null}
                                </span>
                                <span className="min-w-0 flex-1 whitespace-normal">
                                  {countryLabel}
                                </span>
                              </span>
                              <span className="ml-4 font-semibold text-indigo-600">
                                {c.count.toLocaleString()}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    Visits by Page
                  </p>
                  <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    {loading ? (
                      <div className="text-sm text-gray-600">Loading...</div>
                    ) : pageCounts.length === 0 ? (
                      <div className="text-sm text-gray-600">No page data</div>
                    ) : (
                      <ul className="text-sm text-gray-800">
                        {pageCounts.map((p) => (
                          <li
                            key={p.page}
                            className="flex items-center justify-between py-1"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {truncateNullable(p.page, 32)}
                            </span>
                            <span className="ml-4 font-semibold text-indigo-600">
                              {p.count.toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    Visits by Device
                  </p>
                  <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    {loading ? (
                      <div className="text-sm text-gray-600">Loading...</div>
                    ) : deviceCounts.length === 0 ? (
                      <div className="text-sm text-gray-600">
                        No device data
                      </div>
                    ) : (
                      <ul className="text-sm text-gray-800">
                        {deviceCounts.map((d) => (
                          <li
                            key={d.device}
                            className="flex items-center justify-between py-1"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {d.device}
                            </span>
                            <span className="ml-4 font-semibold text-indigo-600">
                              {d.count.toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    Visits by UTM
                  </p>
                  <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    {loading ? (
                      <div className="text-sm text-gray-600">Loading...</div>
                    ) : utmCounts.length === 0 ? (
                      <div className="text-sm text-gray-600">No UTM data</div>
                    ) : (
                      <ul className="text-sm text-gray-800">
                        {utmCounts.slice(0, 6).map((u) => (
                          <li
                            key={u.utm}
                            className="flex items-center justify-between py-1"
                          >
                            <span className="min-w-0 flex-1 truncate">
                              {u.utm}
                            </span>
                            <span className="ml-4 font-semibold text-indigo-600">
                              {u.count.toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {!isMobile && (
            <section className="hidden overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-lg sm:block">
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {title}
                  </h2>
                  <p className="text-sm text-gray-600">{subtitle}</p>
                </div>

                <div className="ml-4 flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={period === 'day' ? 'primary' : 'secondary'}
                    aria-pressed={period === 'day'}
                    onClick={() => setPeriod('day')}
                  >
                    Day
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={period === 'month' ? 'primary' : 'secondary'}
                    aria-pressed={period === 'month'}
                    onClick={() => setPeriod('month')}
                  >
                    Month
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={period === 'year' ? 'primary' : 'secondary'}
                    aria-pressed={period === 'year'}
                    onClick={() => setPeriod('year')}
                  >
                    Year
                  </Button>
                </div>
              </div>
              <div
                ref={chartContainerRef}
                className="mt-4 max-h-[450px] overflow-auto rounded-xl bg-white p-2 sm:p-4"
              >
                {loading ? (
                  <div className="flex h-48 items-center justify-center text-gray-500">
                    Loading daily trend...
                  </div>
                ) : chartPoints.length === 0 ? (
                  <div className="flex h-48 items-center justify-center text-gray-500">
                    No visits recorded yet.
                  </div>
                ) : (
                  <VisitsLineChart points={chartPoints} />
                )}
              </div>
            </section>
          )}

          {/* Mobile: small summary instead of the full Daily Breakdown chart */}
          {isMobile && (
            <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:hidden">
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-1">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {title}
                  </h2>
                  <p className="text-sm text-gray-600">{subtitle}</p>
                </div>
              </div>

              <div className="mt-4 rounded-xl bg-white p-4 pr-6 text-sm text-gray-700">
                {loading ? (
                  <div className="py-8 text-center text-gray-500">
                    Loading daily trend...
                  </div>
                ) : chartPoints.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    No visits recorded yet.
                  </div>
                ) : (
                  <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                        Max
                      </div>
                      <div className="text-sm font-semibold text-gray-900">
                        {Math.max(...chartPoints.map((p) => p.value), 1)}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                        Periods
                      </div>
                      <div className="text-sm font-semibold text-gray-900">
                        {chartPoints.length}
                      </div>
                    </div>
                    <div className="pt-2 text-xs text-gray-500">
                      Full chart is available on tablet and desktop views.
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  Recent Visits
                </h2>
                <p className="text-sm text-gray-600">
                  The latest raw visits with country, device, UTM, and referrer.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={showUsersOnly ? 'secondary' : 'primary'}
                  aria-pressed={!showUsersOnly}
                  onClick={() => setShowUsersOnly(false)}
                >
                  All visitors
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={showUsersOnly ? 'primary' : 'secondary'}
                  aria-pressed={showUsersOnly}
                  onClick={() => setShowUsersOnly(true)}
                >
                  Users only
                </Button>
                <span className="text-xs text-gray-500">
                  {showUsersOnly
                    ? 'Now showing authenticated user visits only'
                    : 'Showing all recorded visitor traffic'}
                </span>
                <div className="ml-2 flex items-center gap-2">
                  <label className="sr-only" htmlFor="referrer-search">
                    Search referrer
                  </label>
                  <input
                    id="referrer-search"
                    type="search"
                    value={referrerQuery}
                    onChange={(e) => setReferrerQuery(e.target.value)}
                    placeholder="Search referrer"
                    className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                  {referrerQuery ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => setReferrerQuery('')}
                    >
                      Clear
                    </Button>
                  ) : null}

                  {referrerQuery ? (
                    <div className="ml-2 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                      {filteredRecentVisits.length.toLocaleString()}{' '}
                      {filteredRecentVisits.length === 1 ? 'match' : 'matches'}
                    </div>
                  ) : null}
                </div>
                <div className="ml-2 flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={'secondary'}
                    className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                    onClick={() => setConfirmingBulk(true)}
                    disabled={selectedIds.length === 0 || isDeletingMultiple}
                  >
                    {isDeletingMultiple
                      ? 'Deleting...'
                      : `Delete selected (${selectedIds.length})`}
                  </Button>
                </div>
                <label className="ml-2 inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideAdminVisits}
                    onChange={(e) =>
                      setHideAdminVisits(e.currentTarget.checked)
                    }
                  />
                  Hide admin users
                </label>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-gray-100">
              {/* Mobile card list */}
              <div className="px-3 py-2 sm:hidden">
                {loading ? (
                  <div className="py-6 text-center text-gray-500">
                    Loading visits...
                  </div>
                ) : filteredRecentVisits.length ? (
                  <div className="space-y-3">
                    {visibleRecentVisits.map((visit) => {
                      const dateTime = formatDateTime(visit.occurred_at);
                      const iso = extractIsoCode(visit.country ?? '');
                      const displayCountry = iso
                        ? getRegionName(iso)
                        : formatNullable(visit.country);
                      const flagEmoji = flagEmojiFromCode(iso ?? '');

                      const deviceFull = formatNullable(visit.device);
                      const isMobileDevice =
                        /android|iphone|ipad|ipod|mobile|mobi/i.test(
                          deviceFull
                        );
                      const deviceDisplay = isMobileDevice
                        ? 'Mobile'
                        : 'Desktop';
                      const pageVisitedLabelMobile = getPageVisitedLabel(
                        visit.referrer
                      );
                      const pageVisitedUrlMobile = getPageVisitedUrl(
                        visit.referrer
                      );

                      return (
                        <div
                          key={visit.id}
                          className={`rounded-lg border p-3 shadow-sm ${
                            visit.user_id
                              ? 'border-indigo-300 bg-indigo-100'
                              : 'border-gray-100 bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-indigo-600"
                                checked={selectedIds.includes(visit.id)}
                                onChange={() => {
                                  setSelectedIds((prev) => {
                                    if (prev.includes(visit.id)) {
                                      return prev.filter(
                                        (id) => id !== visit.id
                                      );
                                    }
                                    return [...prev, visit.id];
                                  });
                                }}
                              />
                              <span className="text-xs text-gray-600">
                                Select
                              </span>
                            </label>
                          </div>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-gray-900">
                                {dateTime.date}
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                {dateTime.time}
                              </div>

                              <div className="mt-3 space-y-1 text-sm text-gray-700">
                                <div>
                                  <span className="font-semibold text-gray-700">
                                    User:
                                  </span>{' '}
                                  {formatNullable(visit.display_name ?? null)}
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-700">
                                    Country:
                                  </span>{' '}
                                  {visit.country ? (
                                    <span className="inline-flex items-center gap-2">
                                      {iso ? (
                                        <img
                                          src={`https://flagcdn.com/24x18/${iso.toLowerCase()}.png`}
                                          alt={displayCountry}
                                          title={displayCountry}
                                          className="h-4 w-6 flex-shrink-0 rounded-sm object-cover"
                                          onError={(e) => {
                                            (
                                              e.currentTarget as HTMLImageElement
                                            ).style.display = 'none';
                                          }}
                                        />
                                      ) : (
                                        <img
                                          src={`https://countryflagsapi.com/png/${encodeURIComponent(visit.country)}`}
                                          alt={displayCountry}
                                          title={displayCountry}
                                          className="h-4 w-6 flex-shrink-0 rounded-sm object-cover"
                                          onError={(e) => {
                                            (
                                              e.currentTarget as HTMLImageElement
                                            ).style.display = 'none';
                                          }}
                                        />
                                      )}
                                      {flagEmoji ? (
                                        <span className="text-sm">
                                          {flagEmoji}
                                        </span>
                                      ) : null}
                                      <span className="truncate">
                                        {displayCountry}
                                      </span>
                                    </span>
                                  ) : (
                                    'N/A'
                                  )}
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-700">
                                    Device:
                                  </span>{' '}
                                  {deviceDisplay}
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-700">
                                    UTM:
                                  </span>{' '}
                                  {formatNullable(visit.utm_source)}
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-700">
                                    Page:
                                  </span>{' '}
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="min-w-0 flex-1 truncate">
                                      {pageVisitedLabelMobile}
                                    </span>
                                    {pageVisitedUrlMobile ? (
                                      <a
                                        href={pageVisitedUrlMobile}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Opens in new tab"
                                        aria-label="Opens in new tab"
                                        className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                                      >
                                        <svg
                                          aria-hidden="true"
                                          className="h-4 w-4"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth={2}
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <path d="M14 3h7v7" />
                                          <path d="M10 14L21 3" />
                                          <path d="M21 21H3V3" />
                                        </svg>
                                        <span className="sr-only">
                                          Opens in new tab
                                        </span>
                                      </a>
                                    ) : null}
                                  </div>
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-700">
                                    Duration:
                                  </span>{' '}
                                  <span className="text-sm text-gray-700">
                                    {(() => {
                                      const refLower = (
                                        visit.referrer || ''
                                      ).toLowerCase();
                                      if (!refLower.includes('/breathing'))
                                        return '—';

                                      let dur: string | null = null;
                                      if (visit.user_id) {
                                        const cached =
                                          sessionsCache[visit.user_id];
                                        if (cached && Array.isArray(cached)) {
                                          const vt = visit.occurred_at
                                            ? new Date(
                                                visit.occurred_at
                                              ).getTime()
                                            : NaN;
                                          if (!Number.isNaN(vt)) {
                                            let best: {
                                              delta: number;
                                              s: BreathingSession;
                                            } | null = null;
                                            for (const s of cached) {
                                              const sessDate =
                                                s.session_date ?? s.created_at;
                                              const st = sessDate
                                                ? new Date(sessDate).getTime()
                                                : NaN;
                                              if (Number.isNaN(st)) continue;
                                              const delta = st - vt;
                                              const durMs =
                                                (s.total_duration || 0) * 1000;
                                              if (
                                                delta >= 0 &&
                                                delta <= durMs + 30000
                                              ) {
                                                if (!best || delta < best.delta)
                                                  best = { delta, s };
                                              }
                                            }
                                            if (!best) {
                                              for (const s of cached) {
                                                const sessDate =
                                                  s.session_date ??
                                                  s.created_at;
                                                const st = sessDate
                                                  ? new Date(sessDate).getTime()
                                                  : NaN;
                                                if (Number.isNaN(st)) continue;
                                                const delta = Math.abs(st - vt);
                                                if (delta <= 5 * 60 * 1000) {
                                                  if (
                                                    !best ||
                                                    delta < best.delta
                                                  )
                                                    best = { delta, s };
                                                }
                                              }
                                            }
                                            if (best)
                                              dur = formatSeconds(
                                                best.s.total_duration || 0
                                              );
                                          }
                                        }
                                      }
                                      if (!dur)
                                        dur = parseDurationFromReferrer(
                                          visit.referrer
                                        );
                                      return dur ? dur : '—';
                                    })()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3">
                            <Button
                              type="button"
                              size="sm"
                              className="w-full !bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                              onClick={() => openConfirm(visit.id)}
                              disabled={deletingId === visit.id}
                            >
                              {deletingId === visit.id
                                ? 'Deleting...'
                                : 'Delete'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-6 text-center text-gray-500">
                    {showUsersOnly
                      ? 'No logged-in user visits yet.'
                      : 'No visits logged yet.'}
                  </div>
                )}
              </div>
            </div>

            {/* Desktop / tablet table view */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="min-w-full text-left text-sm text-gray-800">
                <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-700">
                  <tr>
                    <th className="px-4 py-3" />
                    <th className="px-4 py-3">Date and Time</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Country</th>
                    <th className="px-4 py-3">Device</th>
                    <th className="px-4 py-3">UTM Source</th>
                    <th className="px-4 py-3">Page Visited</th>
                    <th className="px-4 py-3">Duration</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        className="px-4 py-6 text-center text-gray-500"
                        colSpan={9}
                      >
                        Loading visits...
                      </td>
                    </tr>
                  ) : filteredRecentVisits.length ? (
                    visibleRecentVisits.map((visit) => {
                      const dateTime = formatDateTime(visit.occurred_at);
                      const iso = extractIsoCode(visit.country ?? '');
                      const displayCountry = iso
                        ? getRegionName(iso)
                        : formatNullable(visit.country);
                      const flagEmoji = flagEmojiFromCode(iso ?? '');

                      const deviceFull = formatNullable(visit.device);
                      const isMobileDevice =
                        /android|iphone|ipad|ipod|mobile|mobi/i.test(
                          deviceFull
                        );
                      const deviceDisplay = isMobileDevice
                        ? 'Mobile'
                        : 'Desktop';
                      const pageVisitedLabelDesktop = getPageVisitedLabel(
                        visit.referrer
                      );
                      const pageVisitedUrlDesktop = getPageVisitedUrl(
                        visit.referrer
                      );

                      return (
                        <tr
                          key={visit.id}
                          className={`border-t hover:bg-indigo-50 ${
                            visit.user_id
                              ? 'border-indigo-300 bg-indigo-100'
                              : 'border-gray-100'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-indigo-600"
                              checked={selectedIds.includes(visit.id)}
                              onChange={(e) => {
                                const checked = e.currentTarget.checked;
                                setSelectedIds((prev) => {
                                  if (checked) return [...prev, visit.id];
                                  return prev.filter((id) => id !== visit.id);
                                });
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">
                              {dateTime.date}
                            </div>
                            <div className="text-xs text-gray-600">
                              {dateTime.time}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {formatNullable(visit.display_name ?? null)}
                          </td>
                          <td className="px-4 py-3">
                            {visit.country ? (
                              <div className="flex items-center gap-2">
                                {iso ? (
                                  <img
                                    src={`https://flagcdn.com/24x18/${iso.toLowerCase()}.png`}
                                    alt={displayCountry}
                                    title={displayCountry}
                                    className="h-4 w-6 flex-shrink-0 rounded-sm object-cover"
                                    onError={(e) => {
                                      (
                                        e.currentTarget as HTMLImageElement
                                      ).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <img
                                    src={`https://countryflagsapi.com/png/${encodeURIComponent(
                                      visit.country
                                    )}`}
                                    alt={displayCountry}
                                    title={displayCountry}
                                    className="h-4 w-6 flex-shrink-0 rounded-sm object-cover"
                                    onError={(e) => {
                                      (
                                        e.currentTarget as HTMLImageElement
                                      ).style.display = 'none';
                                    }}
                                  />
                                )}
                                {flagEmoji ? (
                                  <span className="text-sm">{flagEmoji}</span>
                                ) : null}
                              </div>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td className="px-4 py-3" title={deviceFull}>
                            <span className="block max-w-[18rem] truncate">
                              {deviceDisplay}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {formatNullable(visit.utm_source)}
                          </td>

                          <td
                            className="px-4 py-3"
                            title={(visit.referrer ?? '').trim() || undefined}
                          >
                            <div className="flex items-center gap-2">
                              <span className="min-w-0 truncate">
                                {pageVisitedLabelDesktop}
                              </span>
                              {pageVisitedUrlDesktop ? (
                                <a
                                  href={pageVisitedUrlDesktop}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Opens in new tab"
                                  aria-label="Opens in new tab"
                                  className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                                >
                                  <svg
                                    aria-hidden="true"
                                    className="h-4 w-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M14 3h7v7" />
                                    <path d="M10 14L21 3" />
                                    <path d="M21 21H3V3" />
                                  </svg>
                                  <span className="sr-only">
                                    Opens in new tab
                                  </span>
                                </a>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {(() => {
                              // Only show duration when referrer explicitly contains /breathing
                              const refLower = (
                                visit.referrer || ''
                              ).toLowerCase();
                              if (!refLower.includes('/breathing')) return '—';

                              // Prefer session-derived duration for signed-in users
                              let dur: string | null = null;
                              if (visit.user_id) {
                                const cached = sessionsCache[visit.user_id];
                                if (cached && Array.isArray(cached)) {
                                  const vt = visit.occurred_at
                                    ? new Date(visit.occurred_at).getTime()
                                    : NaN;
                                  if (!Number.isNaN(vt)) {
                                    let best: {
                                      delta: number;
                                      s: BreathingSession;
                                    } | null = null;
                                    for (const s of cached) {
                                      const sessDate =
                                        s.session_date ?? s.created_at;
                                      const st = sessDate
                                        ? new Date(sessDate).getTime()
                                        : NaN;
                                      if (Number.isNaN(st)) continue;
                                      const delta = st - vt;
                                      const durMs =
                                        (s.total_duration || 0) * 1000;
                                      if (
                                        delta >= 0 &&
                                        delta <= durMs + 30000
                                      ) {
                                        if (!best || delta < best.delta)
                                          best = { delta, s };
                                      }
                                    }
                                    if (!best) {
                                      for (const s of cached) {
                                        const sessDate =
                                          s.session_date ?? s.created_at;
                                        const st = sessDate
                                          ? new Date(sessDate).getTime()
                                          : NaN;
                                        if (Number.isNaN(st)) continue;
                                        const delta = Math.abs(st - vt);
                                        if (delta <= 5 * 60 * 1000) {
                                          if (!best || delta < best.delta)
                                            best = { delta, s };
                                        }
                                      }
                                    }

                                    if (best)
                                      dur = formatSeconds(
                                        best.s.total_duration || 0
                                      );
                                  }
                                }
                              }

                              if (!dur)
                                dur = parseDurationFromReferrer(visit.referrer);
                              return dur ? dur : '—';
                            })()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              type="button"
                              size="sm"
                              className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                              onClick={() => openConfirm(visit.id)}
                              disabled={deletingId === visit.id}
                            >
                              {deletingId === visit.id
                                ? 'Deleting...'
                                : 'Delete'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        className="px-4 py-6 text-center text-gray-500"
                        colSpan={9}
                      >
                        {showUsersOnly
                          ? 'No logged-in user visits yet.'
                          : 'No visits logged yet.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {filteredRecentVisits.length > RECENT_VISITS_PAGE_SIZE ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-600">
                <span>
                  Showing {visibleRecentVisits.length} of{' '}
                  {filteredRecentVisits.length} visits
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  onClick={() =>
                    setVisibleCount((prev) =>
                      Math.min(
                        prev + RECENT_VISITS_PAGE_SIZE,
                        filteredRecentVisits.length
                      )
                    )
                  }
                  disabled={!hasMoreVisits}
                >
                  Show next {RECENT_VISITS_PAGE_SIZE}
                </Button>
              </div>
            ) : null}
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  Dashboard Page Time
                </h2>
                <p className="text-sm text-gray-600">
                  Track how long users keep the dashboard open and how those
                  sessions end.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={loadDashboardPageTime}
                  disabled={pageTimeLoading}
                >
                  {pageTimeLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <span className="text-xs text-gray-500">
                  Showing {visibleDashboardPageTimes.length.toLocaleString()} of{' '}
                  {filteredDashboardPageTimes.length.toLocaleString()}{' '}
                  {filteredDashboardPageTimes.length === 1
                    ? 'entry'
                    : 'entries'}
                </span>
                {hiddenAdminPageTimeCount > 0 ? (
                  <span className="text-xs font-medium text-rose-500">
                    {hiddenAdminPageTimeCount} admin hidden
                  </span>
                ) : null}
                {hiddenInProgressPageTimeCount > 0 ? (
                  <span className="text-xs font-medium text-amber-600">
                    {hiddenInProgressPageTimeCount} in progress hidden
                  </span>
                ) : null}
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideAdminPageTime}
                    onChange={(e) =>
                      setHideAdminPageTime(e.currentTarget.checked)
                    }
                  />
                  Hide admin users
                </label>
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideInProgressPageTime}
                    onChange={(e) =>
                      setHideInProgressPageTime(e.currentTarget.checked)
                    }
                  />
                  Hide still in progress
                </label>
                <div className="flex max-w-xs flex-col gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs">
                  <label
                    htmlFor="dashboard-page-time-visitor-filter"
                    className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Visitor ID
                  </label>
                  <input
                    id="dashboard-page-time-visitor-filter"
                    type="text"
                    value={visitorIdFilter}
                    onChange={(e) => setVisitorIdFilter(e.target.value)}
                    placeholder="Filter entries"
                    className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            {pageTimeError ? (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {pageTimeError}
              </div>
            ) : null}
            <div className="mt-4 rounded-xl border border-gray-100">
              <div className="overflow-x-auto">
                {pageTimeLoading ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    Loading dashboard page time entries...
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th scope="col" className="px-4 py-3">
                          Entered
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Visitor
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Duration
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Exit Type
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Exited
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
                      {visibleDashboardPageTimes.length > 0 ? (
                        visibleDashboardPageTimes.map((entry) => {
                          const entered = formatDateTime(entry.entered_at);
                          const enteredDate = new Date(entry.entered_at);
                          const isEnteredToday = isSameLocalDate(
                            enteredDate,
                            new Date()
                          );
                          const exited = entry.exited_at
                            ? formatDateTime(entry.exited_at)
                            : null;
                          const durationSeconds = computePageTimeSeconds(entry);
                          const durationLabel =
                            durationSeconds !== null
                              ? formatSeconds(durationSeconds)
                              : 'N/A';
                          const userDisplay = formatNullable(
                            entry.display_name ?? null
                          );
                          const visitorIdLabel = formatNullable(
                            entry.visitor_id
                          );
                          const exitLabel = formatNullable(entry.exit_type);
                          const isActive = !entry.exited_at;
                          return (
                            <tr
                              key={entry.id}
                              className="transition-colors hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div
                                  className={`text-gray-900 ${
                                    isEnteredToday
                                      ? 'font-semibold'
                                      : 'font-normal'
                                  }`}
                                >
                                  {entered.date}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {entered.time}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {userDisplay}
                                </div>
                                <div className="break-all text-xs text-gray-500">
                                  {visitorIdLabel}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold text-indigo-600">
                                  {durationLabel}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {isActive ? (
                                  <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-600">
                                    Active
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                                    {exitLabel}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {exited ? (
                                  <>
                                    <div className="font-semibold text-gray-900">
                                      {exited.date}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {exited.time}
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-500">
                                    Still in progress
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="px-4 py-6 text-center text-gray-500"
                            colSpan={5}
                          >
                            {hideAdminPageTime && hiddenAdminPageTimeCount > 0
                              ? 'Only admin dashboard page time entries are available. Uncheck "Hide admin users" to view them.'
                              : hideInProgressPageTime &&
                                  hiddenInProgressPageTimeCount > 0
                                ? 'Only in-progress dashboard sessions are available. Uncheck "Hide still in progress" to view them.'
                                : 'No dashboard page time entries yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {filteredDashboardPageTimes.length > 0 ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-600">
                <span>
                  Showing {visibleDashboardPageTimes.length} of{' '}
                  {filteredDashboardPageTimes.length} entries
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  onClick={() =>
                    setPageTimeVisibleCount((prev) =>
                      Math.min(
                        prev + DASHBOARD_LIST_PAGE_SIZE,
                        filteredDashboardPageTimes.length
                      )
                    )
                  }
                  disabled={!hasMoreDashboardPageTimes}
                >
                  Show next {DASHBOARD_LIST_PAGE_SIZE}
                </Button>
              </div>
            ) : null}
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  Dashboard Events
                </h2>
                <p className="text-sm text-gray-600">
                  Live interactions coming from the dashboard experience.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={loadDashboardEvents}
                  disabled={eventsLoading}
                >
                  {eventsLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <span className="text-xs text-gray-500">
                  Showing {visibleDashboardEvents.length.toLocaleString()} of{' '}
                  {filteredDashboardEvents.length.toLocaleString()} event
                  {filteredDashboardEvents.length === 1 ? '' : 's'}
                </span>
                {hideAdminDashboardEvents &&
                dashboardEvents.length > filteredDashboardEvents.length ? (
                  <span className="text-xs font-medium text-rose-500">
                    {dashboardEvents.length - filteredDashboardEvents.length}{' '}
                    admin hidden
                  </span>
                ) : null}
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideAdminDashboardEvents}
                    onChange={(e) =>
                      setHideAdminDashboardEvents(e.currentTarget.checked)
                    }
                  />
                  Hide admin users
                </label>
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={showStartClickOnly}
                    onChange={(e) =>
                      setShowStartClickOnly(e.currentTarget.checked)
                    }
                  />
                  Only start_click
                </label>
                <div className="flex max-w-xs flex-col gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs">
                  <label
                    htmlFor="dashboard-events-visitor-filter"
                    className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Visitor ID
                  </label>
                  <input
                    id="dashboard-events-visitor-filter"
                    type="text"
                    value={visitorIdFilter}
                    onChange={(e) => setVisitorIdFilter(e.target.value)}
                    placeholder="Filter events"
                    className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            {eventsError ? (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {eventsError}
              </div>
            ) : null}
            <div className="mt-4 rounded-xl border border-gray-100">
              <div className="overflow-x-auto">
                {eventsLoading ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    Loading dashboard events...
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th scope="col" className="px-4 py-3">
                          Created
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Visitor
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Event Type
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Category
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Label
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
                      {visibleDashboardEvents.length > 0 ? (
                        visibleDashboardEvents.map((event) => {
                          const dateTime = formatDateTime(event.created_at);
                          const createdDate = new Date(event.created_at);
                          const isCreatedToday = isSameLocalDate(
                            createdDate,
                            new Date()
                          );
                          const userDisplay = formatNullable(
                            event.display_name ?? null
                          );
                          const userIdLabel = formatNullable(event.user_id);
                          const visitorIdLabel = formatNullable(
                            event.visitor_id
                          );
                          const eventLabel = formatNullable(event.event_label);
                          const eventValue = truncateNullable(
                            event.event_value,
                            80
                          );
                          const primaryVisitorLabel =
                            userDisplay !== 'N/A' ? userDisplay : userIdLabel;
                          return (
                            <tr
                              key={event.id}
                              className="transition-colors hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div
                                  className={`text-gray-900 ${
                                    isCreatedToday
                                      ? 'font-semibold'
                                      : 'font-normal'
                                  }`}
                                >
                                  {dateTime.date}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {dateTime.time}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {primaryVisitorLabel}
                                </div>
                                <div className="break-all text-xs text-gray-500">
                                  {visitorIdLabel}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold text-indigo-600">
                                  {event.event_type}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                                  {event.event_category}
                                </span>
                              </td>
                              <td
                                className="px-4 py-3"
                                title={
                                  (event.event_label ?? '').trim() || undefined
                                }
                              >
                                <span className="block max-w-[16rem] truncate">
                                  {eventLabel}
                                </span>
                              </td>
                              <td
                                className="px-4 py-3"
                                title={
                                  (event.event_value ?? '').trim() || undefined
                                }
                              >
                                <span className="block max-w-[18rem] truncate">
                                  {eventValue}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="px-4 py-6 text-center text-gray-500"
                            colSpan={6}
                          >
                            {hideAdminDashboardEvents &&
                            dashboardEvents.length > 0
                              ? 'Only admin dashboard events are available. Uncheck "Hide admin users" to view them.'
                              : showStartClickOnly && dashboardEvents.length > 0
                                ? 'No start_click events are available. Uncheck "Only start_click" to view them.'
                                : 'No dashboard events recorded yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {filteredDashboardEvents.length > 0 ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-600">
                <span>
                  Showing {visibleDashboardEvents.length} of{' '}
                  {filteredDashboardEvents.length} events
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  onClick={() =>
                    setDashboardEventsVisibleCount((prev) =>
                      Math.min(
                        prev + DASHBOARD_LIST_PAGE_SIZE,
                        filteredDashboardEvents.length
                      )
                    )
                  }
                  disabled={!hasMoreDashboardEvents}
                >
                  Show next {DASHBOARD_LIST_PAGE_SIZE}
                </Button>
              </div>
            ) : null}
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  Breathing Page Time
                </h2>
                <p className="text-sm text-gray-600">
                  Track how long users keep the breathing page open and how
                  those sessions end.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={loadBreathingPageTime}
                  disabled={breathingPageTimeLoading}
                >
                  {breathingPageTimeLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <span className="text-xs text-gray-500">
                  Showing {visibleBreathingPageTimes.length.toLocaleString()} of{' '}
                  {filteredBreathingPageTimes.length.toLocaleString()}{' '}
                  {filteredBreathingPageTimes.length === 1
                    ? 'entry'
                    : 'entries'}
                </span>
                {hiddenAdminBreathingPageTimeCount > 0 ? (
                  <span className="text-xs font-medium text-rose-500">
                    {hiddenAdminBreathingPageTimeCount} admin hidden
                  </span>
                ) : null}
                {hiddenInProgressBreathingPageTimeCount > 0 ? (
                  <span className="text-xs font-medium text-amber-600">
                    {hiddenInProgressBreathingPageTimeCount} in progress hidden
                  </span>
                ) : null}
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideAdminBreathingPageTime}
                    onChange={(e) =>
                      setHideAdminBreathingPageTime(e.currentTarget.checked)
                    }
                  />
                  Hide admin users
                </label>
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideInProgressBreathingPageTime}
                    onChange={(e) =>
                      setHideInProgressBreathingPageTime(
                        e.currentTarget.checked
                      )
                    }
                  />
                  Hide still in progress
                </label>
                <div className="flex max-w-xs flex-col gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs">
                  <label
                    htmlFor="breathing-page-time-visitor-filter"
                    className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Visitor ID
                  </label>
                  <input
                    id="breathing-page-time-visitor-filter"
                    type="text"
                    value={visitorIdFilter}
                    onChange={(e) => setVisitorIdFilter(e.target.value)}
                    placeholder="Filter entries"
                    className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            {breathingPageTimeError ? (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {breathingPageTimeError}
              </div>
            ) : null}
            <div className="mt-4 rounded-xl border border-gray-100">
              <div className="overflow-x-auto">
                {breathingPageTimeLoading ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    Loading breathing page time...
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th scope="col" className="px-4 py-3">
                          Entered
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Visitor
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Duration
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Exit Type
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Exited
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
                      {visibleBreathingPageTimes.length > 0 ? (
                        visibleBreathingPageTimes.map((entry) => {
                          const entered = formatDateTime(entry.entered_at);
                          const enteredDate = new Date(entry.entered_at);
                          const isEnteredToday = isSameLocalDate(
                            enteredDate,
                            new Date()
                          );
                          const exited = entry.exited_at
                            ? formatDateTime(entry.exited_at)
                            : null;
                          const userDisplay = formatNullable(
                            entry.display_name ?? null
                          );
                          const userIdLabel = formatNullable(entry.user_id);
                          const visitorIdLabel = formatNullable(
                            entry.visitor_id
                          );
                          const durationSeconds = computePageTimeSeconds(entry);
                          const exitTypeLabel = formatNullable(entry.exit_type);
                          const primaryVisitorLabel =
                            userDisplay !== 'N/A' ? userDisplay : userIdLabel;

                          return (
                            <tr
                              key={entry.id}
                              className="transition-colors hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div
                                  className={`text-gray-900 ${
                                    isEnteredToday
                                      ? 'font-semibold'
                                      : 'font-normal'
                                  }`}
                                >
                                  {entered.date}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {entered.time}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {primaryVisitorLabel}
                                </div>
                                <div className="break-all text-xs text-gray-500">
                                  {visitorIdLabel}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold text-indigo-600">
                                  {formatSeconds(durationSeconds)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                                  {exitTypeLabel}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {exited ? (
                                  <>
                                    <div className="font-semibold text-gray-900">
                                      {exited.date}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {exited.time}
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-500">
                                    In progress
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="px-4 py-6 text-center text-gray-500"
                            colSpan={5}
                          >
                            {hideAdminBreathingPageTime &&
                            breathingPageTimeEntries.length > 0
                              ? 'Only admin breathing page time entries are available. Uncheck "Hide admin users" to view them.'
                              : hideInProgressBreathingPageTime &&
                                  hiddenInProgressBreathingPageTimeCount > 0
                                ? 'Only in-progress breathing sessions are available. Uncheck "Hide still in progress" to view them.'
                                : 'No breathing page time entries yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {filteredBreathingPageTimes.length > 0 ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-600">
                <span>
                  Showing {visibleBreathingPageTimes.length} of{' '}
                  {filteredBreathingPageTimes.length} entries
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  onClick={() =>
                    setBreathingPageTimeVisibleCount((prev) =>
                      Math.min(
                        prev + DASHBOARD_LIST_PAGE_SIZE,
                        filteredBreathingPageTimes.length
                      )
                    )
                  }
                  disabled={!hasMoreBreathingPageTimes}
                >
                  Show next {DASHBOARD_LIST_PAGE_SIZE}
                </Button>
              </div>
            ) : null}
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  Breathing Events
                </h2>
                <p className="text-sm text-gray-600">
                  Interaction and lifecycle events from the breathing session.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={loadBreathingEvents}
                  disabled={breathingEventsLoading}
                >
                  {breathingEventsLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <span className="text-xs text-gray-500">
                  Showing {visibleBreathingEvents.length.toLocaleString()} of{' '}
                  {filteredBreathingEvents.length.toLocaleString()} event
                  {filteredBreathingEvents.length === 1 ? '' : 's'}
                </span>
                {hideAdminBreathingEvents &&
                breathingEvents.length > filteredBreathingEvents.length ? (
                  <span className="text-xs font-medium text-rose-500">
                    {breathingEvents.length - filteredBreathingEvents.length}{' '}
                    admin hidden
                  </span>
                ) : null}
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideAdminBreathingEvents}
                    onChange={(e) =>
                      setHideAdminBreathingEvents(e.currentTarget.checked)
                    }
                  />
                  Hide admin users
                </label>
                <div className="flex max-w-xs flex-col gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs">
                  <label
                    htmlFor="breathing-events-visitor-filter"
                    className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Visitor ID
                  </label>
                  <input
                    id="breathing-events-visitor-filter"
                    type="text"
                    value={visitorIdFilter}
                    onChange={(e) => setVisitorIdFilter(e.target.value)}
                    placeholder="Filter events"
                    className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            {breathingEventsError ? (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {breathingEventsError}
              </div>
            ) : null}
            <div className="mt-4 rounded-xl border border-gray-100">
              <div className="overflow-x-auto">
                {breathingEventsLoading ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    Loading breathing events...
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th scope="col" className="px-4 py-3">
                          Created
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Visitor
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Event Type
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Category
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Label
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
                      {visibleBreathingEvents.length > 0 ? (
                        visibleBreathingEvents.map((event) => {
                          const dateTime = formatDateTime(event.created_at);
                          const createdDate = new Date(event.created_at);
                          const isCreatedToday = isSameLocalDate(
                            createdDate,
                            new Date()
                          );
                          const userDisplay = formatNullable(
                            event.display_name ?? null
                          );
                          const userIdLabel = formatNullable(event.user_id);
                          const visitorIdLabel = formatNullable(
                            event.visitor_id
                          );
                          const sessionIdLabel = formatNullable(
                            event.session_id
                          );
                          const eventLabel = formatNullable(event.event_label);
                          const eventValue = truncateNullable(
                            event.event_value,
                            80
                          );
                          const primaryVisitorLabel =
                            userDisplay !== 'N/A' ? userDisplay : userIdLabel;
                          return (
                            <tr
                              key={event.id}
                              className="transition-colors hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div
                                  className={`text-gray-900 ${
                                    isCreatedToday
                                      ? 'font-semibold'
                                      : 'font-normal'
                                  }`}
                                >
                                  {dateTime.date}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {dateTime.time}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {primaryVisitorLabel}
                                </div>
                                <div className="break-all text-xs text-gray-500">
                                  {visitorIdLabel}
                                </div>
                                <div className="break-all text-[11px] text-gray-400">
                                  {sessionIdLabel}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold text-indigo-600">
                                  {event.event_type}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                                  {event.event_category}
                                </span>
                              </td>
                              <td
                                className="px-4 py-3"
                                title={
                                  (event.event_label ?? '').trim() || undefined
                                }
                              >
                                <span className="block max-w-[16rem] truncate">
                                  {eventLabel}
                                </span>
                              </td>
                              <td
                                className="px-4 py-3"
                                title={
                                  (event.event_value ?? '').trim() || undefined
                                }
                              >
                                <span className="block max-w-[18rem] truncate">
                                  {eventValue}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="px-4 py-6 text-center text-gray-500"
                            colSpan={6}
                          >
                            {hideAdminBreathingEvents &&
                            breathingEvents.length > 0
                              ? 'Only admin breathing events are available. Uncheck "Hide admin users" to view them.'
                              : 'No breathing events recorded yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {filteredBreathingEvents.length > 0 ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-600">
                <span>
                  Showing {visibleBreathingEvents.length} of{' '}
                  {filteredBreathingEvents.length} events
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  onClick={() =>
                    setBreathingEventsVisibleCount((prev) =>
                      Math.min(
                        prev + DASHBOARD_LIST_PAGE_SIZE,
                        filteredBreathingEvents.length
                      )
                    )
                  }
                  disabled={!hasMoreBreathingEvents}
                >
                  Show next {DASHBOARD_LIST_PAGE_SIZE}
                </Button>
              </div>
            ) : null}
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  Vision Board Page Time
                </h2>
                <p className="text-sm text-gray-600">
                  Track how long visitors keep the vision board open.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={loadVisionBoardPageTime}
                  disabled={visionBoardPageTimeLoading}
                >
                  {visionBoardPageTimeLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <span className="text-xs text-gray-500">
                  Showing {visibleVisionBoardPageTimes.length.toLocaleString()}{' '}
                  of {filteredVisionBoardPageTimes.length.toLocaleString()}{' '}
                  {filteredVisionBoardPageTimes.length === 1
                    ? 'entry'
                    : 'entries'}
                </span>
                {hiddenAdminVisionBoardPageTimeCount > 0 ? (
                  <span className="text-xs font-medium text-rose-500">
                    {hiddenAdminVisionBoardPageTimeCount} admin hidden
                  </span>
                ) : null}
                {hiddenInProgressVisionBoardPageTimeCount > 0 ? (
                  <span className="text-xs font-medium text-amber-600">
                    {hiddenInProgressVisionBoardPageTimeCount} in progress
                    hidden
                  </span>
                ) : null}
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideAdminVisionBoardPageTime}
                    onChange={(e) =>
                      setHideAdminVisionBoardPageTime(e.currentTarget.checked)
                    }
                  />
                  Hide admin users
                </label>
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideInProgressVisionBoardPageTime}
                    onChange={(e) =>
                      setHideInProgressVisionBoardPageTime(
                        e.currentTarget.checked
                      )
                    }
                  />
                  Hide in progress
                </label>
                <div className="flex max-w-xs flex-col gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs">
                  <label
                    htmlFor="vision-board-page-time-visitor-filter"
                    className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Visitor ID
                  </label>
                  <input
                    id="vision-board-page-time-visitor-filter"
                    type="text"
                    value={visitorIdFilter}
                    onChange={(e) => setVisitorIdFilter(e.target.value)}
                    placeholder="Filter entries"
                    className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            {visionBoardPageTimeError ? (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {visionBoardPageTimeError}
              </div>
            ) : null}
            <div className="mt-4 rounded-xl border border-gray-100">
              <div className="overflow-x-auto">
                {visionBoardPageTimeLoading ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    Loading vision board page time...
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th scope="col" className="px-4 py-3">
                          Entered
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Visitor
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Duration
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Exit Type
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Exited
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
                      {visibleVisionBoardPageTimes.length > 0 ? (
                        visibleVisionBoardPageTimes.map((entry) => {
                          const entered = formatDateTime(entry.entered_at);
                          const exited = entry.exited_at
                            ? formatDateTime(entry.exited_at)
                            : null;
                          const enteredDate = new Date(entry.entered_at);
                          const isEnteredToday = isSameLocalDate(
                            enteredDate,
                            new Date()
                          );
                          const durationSeconds = entry.duration_seconds ?? 0;
                          const exitTypeLabel = formatNullable(entry.exit_type);
                          const userDisplay = formatNullable(
                            entry.display_name ?? null
                          );
                          const userIdLabel = formatNullable(entry.user_id);
                          const visitorIdLabel = formatNullable(
                            entry.visitor_id
                          );
                          const primaryVisitorLabel =
                            userDisplay !== 'N/A' ? userDisplay : userIdLabel;
                          return (
                            <tr
                              key={entry.id}
                              className="transition-colors hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div
                                  className={`text-gray-900 ${
                                    isEnteredToday
                                      ? 'font-semibold'
                                      : 'font-normal'
                                  }`}
                                >
                                  {entered.date}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {entered.time}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {primaryVisitorLabel}
                                </div>
                                <div className="break-all text-xs text-gray-500">
                                  {visitorIdLabel}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold text-indigo-600">
                                  {formatSeconds(durationSeconds)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                                  {exitTypeLabel}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {exited ? (
                                  <>
                                    <div className="font-semibold text-gray-900">
                                      {exited.date}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      {exited.time}
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-xs text-gray-500">
                                    In progress
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="px-4 py-6 text-center text-gray-500"
                            colSpan={5}
                          >
                            {hideAdminVisionBoardPageTime &&
                            visionBoardPageTimeEntries.length > 0
                              ? 'Only admin vision board page time entries are available. Uncheck "Hide admin users" to view them.'
                              : hideInProgressVisionBoardPageTime &&
                                  hiddenInProgressVisionBoardPageTimeCount > 0
                                ? 'Only in-progress vision board sessions are available. Uncheck "Hide still in progress" to view them.'
                                : 'No vision board page time entries yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {filteredVisionBoardPageTimes.length > 0 ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-600">
                <span>
                  Showing {visibleVisionBoardPageTimes.length} of{' '}
                  {filteredVisionBoardPageTimes.length} entries
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  onClick={() =>
                    setVisionBoardPageTimeVisibleCount((prev) =>
                      Math.min(
                        prev + DASHBOARD_LIST_PAGE_SIZE,
                        filteredVisionBoardPageTimes.length
                      )
                    )
                  }
                  disabled={!hasMoreVisionBoardPageTimes}
                >
                  Show next {DASHBOARD_LIST_PAGE_SIZE}
                </Button>
              </div>
            ) : null}
          </section>

          <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-lg sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-gray-900">
                  Vision Board Events
                </h2>
                <p className="text-sm text-gray-600">
                  Interaction and lifecycle events from vision board sessions.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={loadVisionBoardEvents}
                  disabled={visionBoardEventsLoading}
                >
                  {visionBoardEventsLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <span className="text-xs text-gray-500">
                  Showing {visibleVisionBoardEvents.length.toLocaleString()} of{' '}
                  {filteredVisionBoardEvents.length.toLocaleString()} event
                  {filteredVisionBoardEvents.length === 1 ? '' : 's'}
                </span>
                {hideAdminVisionBoardEvents &&
                visionBoardEvents.length > filteredVisionBoardEvents.length ? (
                  <span className="text-xs font-medium text-rose-500">
                    {visionBoardEvents.length -
                      filteredVisionBoardEvents.length}{' '}
                    admin hidden
                  </span>
                ) : null}
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-indigo-600"
                    checked={hideAdminVisionBoardEvents}
                    onChange={(e) =>
                      setHideAdminVisionBoardEvents(e.currentTarget.checked)
                    }
                  />
                  Hide admin users
                </label>
                <div className="flex max-w-xs flex-col gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs">
                  <label
                    htmlFor="vision-board-events-visitor-filter"
                    className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Visitor ID
                  </label>
                  <input
                    id="vision-board-events-visitor-filter"
                    type="text"
                    value={visitorIdFilter}
                    onChange={(e) => setVisitorIdFilter(e.target.value)}
                    placeholder="Filter events"
                    className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            {visionBoardEventsError ? (
              <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {visionBoardEventsError}
              </div>
            ) : null}
            <div className="mt-4 rounded-xl border border-gray-100">
              <div className="overflow-x-auto">
                {visionBoardEventsLoading ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    Loading vision board events...
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th scope="col" className="px-4 py-3">
                          Created
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Visitor
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Event Type
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Category
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Label
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white text-gray-700">
                      {visibleVisionBoardEvents.length > 0 ? (
                        visibleVisionBoardEvents.map((event) => {
                          const dateTime = formatDateTime(event.created_at);
                          const createdDate = new Date(event.created_at);
                          const isCreatedToday = isSameLocalDate(
                            createdDate,
                            new Date()
                          );
                          const userDisplay = formatNullable(
                            event.display_name ?? null
                          );
                          const userIdLabel = formatNullable(event.user_id);
                          const visitorIdLabel = formatNullable(
                            event.visitor_id
                          );
                          const sessionIdLabel = formatNullable(
                            event.session_id
                          );
                          const eventLabel = formatNullable(event.event_label);
                          const eventValue = truncateNullable(
                            event.event_value,
                            80
                          );
                          const primaryVisitorLabel =
                            userDisplay !== 'N/A' ? userDisplay : userIdLabel;
                          return (
                            <tr
                              key={event.id}
                              className="transition-colors hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div
                                  className={`text-gray-900 ${
                                    isCreatedToday
                                      ? 'font-semibold'
                                      : 'font-normal'
                                  }`}
                                >
                                  {dateTime.date}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {dateTime.time}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">
                                  {primaryVisitorLabel}
                                </div>
                                <div className="break-all text-xs text-gray-500">
                                  {visitorIdLabel}
                                </div>
                                <div className="break-all text-[11px] text-gray-400">
                                  {sessionIdLabel}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold text-indigo-600">
                                  {event.event_type}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700">
                                  {event.event_category}
                                </span>
                              </td>
                              <td
                                className="px-4 py-3"
                                title={
                                  (event.event_label ?? '').trim() || undefined
                                }
                              >
                                <span className="block max-w-[16rem] truncate">
                                  {eventLabel}
                                </span>
                              </td>
                              <td
                                className="px-4 py-3"
                                title={
                                  (event.event_value ?? '').trim() || undefined
                                }
                              >
                                <span className="block max-w-[18rem] truncate">
                                  {eventValue}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            className="px-4 py-6 text-center text-gray-500"
                            colSpan={6}
                          >
                            {hideAdminVisionBoardEvents &&
                            visionBoardEvents.length > 0
                              ? 'Only admin vision board events are available. Uncheck "Hide admin users" to view them.'
                              : 'No vision board events recorded yet.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {filteredVisionBoardEvents.length > 0 ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-600">
                <span>
                  Showing {visibleVisionBoardEvents.length} of{' '}
                  {filteredVisionBoardEvents.length} events
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  onClick={() =>
                    setVisionBoardEventsVisibleCount((prev) =>
                      Math.min(
                        prev + DASHBOARD_LIST_PAGE_SIZE,
                        filteredVisionBoardEvents.length
                      )
                    )
                  }
                  disabled={!hasMoreVisionBoardEvents}
                >
                  Show next {DASHBOARD_LIST_PAGE_SIZE}
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </Container>
      {confirmingVisitId && (
        <Modal title="Confirm deletion" onClose={closeConfirm}>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to delete this visit? This action cannot be
              undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                size="sm"
                onClick={closeConfirm}
                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                onClick={async () => {
                  const id = confirmingVisitId;
                  if (!id) return;
                  await handleDelete(id);
                  closeConfirm();
                }}
                disabled={deletingId !== null}
              >
                {deletingId ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {confirmingBulk && (
        <Modal
          title="Confirm deletion"
          onClose={() => setConfirmingBulk(false)}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to delete {selectedIds.length} selected
              visit(s)? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                size="sm"
                onClick={() => setConfirmingBulk(false)}
                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                disabled={isDeletingMultiple}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
                onClick={async () => {
                  const ids = [...selectedIds];
                  if (ids.length === 0) return;
                  await handleBulkDelete(ids);
                }}
                disabled={isDeletingMultiple}
              >
                {isDeletingMultiple
                  ? 'Deleting...'
                  : `Delete (${selectedIds.length})`}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function VisitsLineChart({ points }: { points: ChartPoint[] }) {
  if (points.length === 0) return null;

  const data = [...points]
    .slice()
    .reverse()
    .map((point) => ({
      date: point.axisDate,
      total: point.value,
      pageVisits: point.value,
    }));

  return <DailyVisitsChart data={data} height={320} />;
}

type DailyVisitDatum = { date: string; total: number; pageVisits: number };

function DailyVisitsChart({
  data,
  height = 220,
}: {
  data: DailyVisitDatum[];
  height?: number;
}) {
  const [hover, setHover] = useState<{
    i: number;
    x: number;
    y: number;
  } | null>(null);

  // Use a ref to the container so we can position the tooltip using mouse coordinates
  const containerRef = useRef<HTMLDivElement | null>(null);

  if (!data || data.length === 0) {
    return <div className="text-sm text-gray-500">No data</div>;
  }

  const chronological = [...data].reverse();
  const values = chronological.map((d) => d.total);
  const max = Math.max(...values, 1);
  const min = 0;
  const padding = 24;
  const pointSpacing =
    chronological.length <= 1
      ? 0
      : chronological.length <= 7
        ? 110
        : chronological.length <= 14
          ? 70
          : chronological.length <= 30
            ? 40
            : 30;
  const w = Math.max(
    (chronological.length - 1) * pointSpacing + padding * 2,
    400
  );
  const h = height;

  const scaleX = (i: number) => {
    if (chronological.length === 1) {
      return padding + (w - padding * 2) / 2;
    }
    return padding + (i / (chronological.length - 1)) * (w - padding * 2);
  };

  const scaleY = (v: number) => {
    if (max === min) return h - padding;
    const ratio = (v - min) / (max - min);
    return h - padding - ratio * (h - padding * 2);
  };

  const points = chronological.map((d, i) => [scaleX(i), scaleY(d.total)]);
  const pathD = points
    .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
    .join(' ');
  let areaD = '';
  if (points.length > 0) {
    const start = points[0]!;
    const end = points[points.length - 1]!;
    areaD =
      `M ${start[0]} ${h - padding} ` +
      points.map((p) => `L ${p[0]} ${p[1]}`).join(' ') +
      ` L ${end[0]} ${h - padding} Z`;
  }

  const ticks = 5;
  const yTicks = Array.from({ length: ticks + 1 }, (_, i) => {
    const value = min + ((max - min) * i) / ticks;
    return { value, y: scaleY(value) };
  });

  return (
    <div className="w-full">
      <div ref={containerRef} style={{ minWidth: w }} className="relative">
        <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="block">
          {yTicks.map((t, i) => (
            <line
              key={`grid-${i}`}
              x1={padding}
              x2={w - padding}
              y1={t.y}
              y2={t.y}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
          ))}
          {areaD ? (
            <>
              <defs>
                <linearGradient id="areaGrad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
                </linearGradient>
              </defs>
              <path d={areaD} fill="url(#areaGrad)" opacity={0.5} />
            </>
          ) : null}
          <path
            d={pathD}
            fill="none"
            stroke="#2563eb"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {points.map((p, i) => {
            const cx = p[0] ?? 0;
            const cy = p[1] ?? 0;
            return (
              <circle
                key={`point-${i}`}
                cx={cx}
                cy={cy}
                r={4}
                fill="#ffffff"
                stroke="#2563eb"
                strokeWidth={2}
                onMouseEnter={(e) => {
                  const rect = containerRef.current?.getBoundingClientRect();
                  const left = rect ? e.clientX - rect.left : cx;
                  const top = rect ? e.clientY - rect.top : cy;
                  setHover({ i, x: left, y: top });
                }}
                onMouseMove={(e) => {
                  const rect = containerRef.current?.getBoundingClientRect();
                  const left = rect ? e.clientX - rect.left : cx;
                  const top = rect ? e.clientY - rect.top : cy;
                  setHover({ i, x: left, y: top });
                }}
                onMouseLeave={() => setHover(null)}
              />
            );
          })}
          {chronological.map((d, i) => {
            const [yearRaw, monthRaw, dayRaw] = d.date.split('-');
            const year = Number(yearRaw) || 1970;
            const month = Number(monthRaw) || 1;
            const day = Number(dayRaw) || 1;
            const dateObj = new Date(year, month - 1, day);
            const label = dateObj.toLocaleDateString(undefined, {
              month: 'numeric',
              day: 'numeric',
            });
            return (
              <text
                key={`x-${i}`}
                x={scaleX(i)}
                y={h - 4}
                textAnchor="middle"
                fontSize={12}
                fill="#4b5563"
              >
                {label}
              </text>
            );
          })}
          {yTicks.map((t, i) => (
            <text key={`y-${i}`} x={8} y={t.y + 4} fontSize={11} fill="#6b7280">
              {Math.round(t.value)}
            </text>
          ))}
        </svg>

        {/* Tooltip */}
        {hover !== null
          ? (() => {
              const idx = hover.i;
              const d = chronological[idx];
              if (!d) return null;
              const left = Math.max(8, Math.min(w - 8, hover.x));
              // Position tooltip so it sits fully above the hovered circle.
              // Subtract the visual highlight radius + a small gap from mouse Y,
              // then clamp to the SVG bounds.
              const gap = 8;
              const circleRadius = 6; // matches the highlight circle radius
              const bottomY = hover.y - (circleRadius + gap);
              const top = Math.max(8, Math.min(h - 8, bottomY));
              const prettyDate = new Date(d.date).toLocaleDateString(
                undefined,
                {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                }
              );
              return (
                <div
                  style={{ left: left, top: top }}
                  className="pointer-events-none absolute z-50 w-40 -translate-x-1/2 -translate-y-full transform"
                >
                  <div className="rounded-md border bg-white px-3 py-2 text-xs text-gray-800 shadow-md">
                    <div className="font-medium">{prettyDate}</div>
                    <div className="mt-1 text-sm font-semibold text-indigo-600">
                      {d.total.toLocaleString()} visits
                    </div>
                  </div>
                </div>
              );
            })()
          : null}

        {/* Highlight circle */}
        {hover !== null
          ? (() => {
              const p = points[hover.i];
              if (!p) return null;
              return (
                <svg
                  className="pointer-events-none absolute left-0 top-0"
                  viewBox={`0 0 ${w} ${h}`}
                  width={w}
                  height={h}
                >
                  <circle
                    cx={p[0]}
                    cy={p[1]}
                    r={6}
                    fill="#2563eb"
                    fillOpacity={0.12}
                    stroke="#2563eb"
                    strokeWidth={1}
                  />
                </svg>
              );
            })()
          : null}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-4 text-xs text-gray-500">
        <span>Max: {max}</span>
        <span>Days: {chronological.length}</span>
      </div>
    </div>
  );
}

function formatDateTime(value: string): { date: string; time: string } {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: '--', time: '' };
  }

  const dateLabel = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);

  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

  return { date: dateLabel, time: timeLabel };
}

function isSameLocalDate(a: Date, b: Date): boolean {
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return false;
  }
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatNullable(value: string | null): string {
  const trimmed = (value || '').trim();
  return trimmed.length > 0 ? trimmed : 'N/A';
}

function truncateNullable(value: string | null | undefined, max = 50): string {
  const v = (value ?? '').trim();
  if (!v) return 'N/A';
  return v.length > max ? `${v.slice(0, max)}…` : v;
}

function getPageVisitedLabel(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) return 'N/A';

  if (!raw.toLowerCase().includes('xhalin.com')) {
    return raw.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  }

  try {
    const u = new URL(raw);
    const head = u.pathname.split('/').filter(Boolean)[0];
    return head ? `/${head}` : '/';
  } catch {
    return truncateNullable(value, 50);
  }
}

function getPageVisitedUrl(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim();
  if (!raw) return null;

  try {
    const url = new URL(raw, 'https://xhalin.com');
    const proto = url.protocol.toLowerCase();
    if (proto !== 'http:' && proto !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

function normalizeDateKey(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

function formatSeconds(totalSeconds: number | null | undefined): string {
  const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
  if (s <= 0) return '0m 0s';
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
}

function computePageTimeSeconds(entry: {
  duration_seconds: number | null;
  entered_at: string;
  exited_at: string | null;
}): number | null {
  if (
    typeof entry.duration_seconds === 'number' &&
    Number.isFinite(entry.duration_seconds)
  ) {
    return Math.max(0, entry.duration_seconds);
  }
  if (!entry.entered_at || !entry.exited_at) return null;
  const entered = new Date(entry.entered_at).getTime();
  const exited = new Date(entry.exited_at).getTime();
  if (Number.isNaN(entered) || Number.isNaN(exited)) return null;
  const diff = exited - entered;
  if (diff < 0) return null;
  return Math.max(0, Math.round(diff / 1000));
}

function parseDurationFromReferrer(
  value: string | null | undefined
): string | null {
  if (!value) return null;

  const raw = String(value || '').trim();
  if (!raw) return null;

  // The quick guard requested: only parse durations for breathing page referrers
  try {
    const url = new URL(raw);
    if (!url.pathname.toLowerCase().includes('/breathing')) return null;

    const params = url.searchParams;
    const minuteKeys = ['duration', 'minutes', 'mins', 'min', 'm', 'd', 't'];
    const secondKeys = [
      'seconds',
      'secs',
      's',
      'total_seconds',
      'totalSeconds',
      'total_duration',
      'totalDuration',
      'session_seconds',
    ];

    for (const k of minuteKeys) {
      if (params.has(k)) {
        const v = params.get(k);
        if (!v) continue;
        if (v.includes(':')) {
          const parts = v.split(':').map((p) => Number(p));
          if (
            parts.length === 2 &&
            parts[0] !== undefined &&
            parts[1] !== undefined &&
            Number.isFinite(parts[0]) &&
            Number.isFinite(parts[1])
          ) {
            const a = Number(parts[0]);
            const b = Number(parts[1]);
            return formatSeconds(a * 60 + b);
          }
        }
        const n = Number(v);
        if (Number.isFinite(n)) return formatSeconds(Math.round(n * 60));
      }
    }

    for (const k of secondKeys) {
      if (params.has(k)) {
        const v = params.get(k);
        if (!v) continue;
        const n = Number(v);
        if (Number.isFinite(n)) return formatSeconds(Math.round(n));
      }
    }

    // Try hash fragment like #duration=5
    if (url.hash) {
      try {
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
        for (const k of minuteKeys) {
          if (hashParams.has(k)) {
            const v = hashParams.get(k);
            if (!v) continue;
            const n = Number(v);
            if (Number.isFinite(n)) return formatSeconds(Math.round(n * 60));
          }
        }
        for (const k of secondKeys) {
          if (hashParams.has(k)) {
            const v = hashParams.get(k);
            if (!v) continue;
            const n = Number(v);
            if (Number.isFinite(n)) return formatSeconds(Math.round(n));
          }
        }
      } catch {
        // fallthrough
      }
    }

    // Fallback: look for a trailing numeric segment after /breathing/ (e.g. /breathing/5 or /breathing/300s)
    const pathParts = url.pathname.split('/').filter(Boolean);
    const idx = pathParts.findIndex((p) => p.toLowerCase() === 'breathing');
    if (idx >= 0 && pathParts.length > idx + 1) {
      const candidate = pathParts[idx + 1];
      if (candidate) {
        const m = candidate.match(/^([0-9]+)(s|m)?$/i);
        if (m) {
          const num = Number(m[1]);
          const unit = (m[2] || '').toLowerCase();
          if (unit === 's') return formatSeconds(num);
          return formatSeconds(num * 60);
        }
      }
    }
  } catch (e) {
    // ignore parsing errors
  }

  return null;
}

function flagEmojiFromCode(code: string): string {
  const c = (code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return '';
  return Array.from(c)
    .map((ch) => String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 65))
    .join('');
}

function getRegionName(code: string): string {
  try {
    const c = (code || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(c)) return code || 'Unknown';
    // Intl.DisplayNames provides localized region names from ISO codes
    // Fallback to the code if not available
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const dn = new Intl.DisplayNames(undefined, { type: 'region' });
    const name = dn.of(c);
    return name || c;
  } catch {
    return code || 'Unknown';
  }
}

function extractIsoCode(value: string): string | null {
  if (!value) return null;
  const m = value.trim().match(/\b([A-Za-z]{2})\b/);
  const code = m?.[1];
  return code ? code.toUpperCase() : null;
}

export default AdminVisits;
